<template>
  <div class="files-page flex flex-col h-full">
    <div class="files-page__header flex-shrink-0 mt-4">
      <!-- Page title (Taskk-style, big & bold) -->
      <div class="files-page__title-row">
        <h1 class="files-page__title">{{ pageTitle }}</h1>
        <span class="files-page__count">{{ filteredTotalFiles }}</span>
        <span class="files-page__dot"></span>
        <span class="files-page__duration">
          <span class="files-page__duration-label">Общая длительность</span>
          <span class="files-page__duration-value">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5V12l3 2" />
            </svg>
            {{ timeNormalizer(filteredTotalDuration) }}
          </span>
        </span>
      </div>

      <!-- Filters row -->
      <div class="files-page__filters">
        <div class="flex flex-wrap items-center gap-3">
          <el-select
            v-model="filters.annotator"
            @change="getFiles"
            placeholder="Аннотатор"
            clearable
            filterable
            style="max-width: 200px"
          >
            <el-option
              v-for="u in users"
              :key="u._id"
              :value="u._id"
              :label="`${u.first_name} ${u.last_name}`"
            ></el-option>
          </el-select>
          <el-select
            v-if="user.scopes.includes('admin')"
            v-model="filters.batch_id"
            @change="getFiles"
            placeholder="Батч"
            clearable
            style="max-width: 200px"
          >
            <el-option
              v-for="batch in batches"
              :key="batch._id"
              :value="batch._id"
              :label="batch.batch_name"
            >
              <template #default>
                <div class="w-full flex items-center justify-between">
                  <span class="text-medium text-gray-600">{{
                    batch.batch_name || "-"
                  }}</span>
                  <span
                    class="ml-3 h-2.5 w-2.5 rounded-full"
                    :class="batch.is_active ? 'bg-green-500' : 'bg-gray-400'"
                    style="font-size: 12.5px"
                  >
                  </span>
                </div>
              </template>
            </el-option>
          </el-select>
          <el-input
            v-model="filters.file_id"
            @change="getFiles"
            placeholder="ID файла"
            clearable
            style="max-width: 200px"
          ></el-input>
          <el-date-picker
            value-format="YYYY-MM-DD"
            v-model="filters.date"
            @change="getFiles"
            placeholder="Выберите отрезок времени"
            type="daterange"
            style="max-width: 200px"
          ></el-date-picker>
          <el-select
            v-model="filters.lang"
            @change="getFiles"
            placeholder="Язык"
            clearable
            style="max-width: 200px"
          >
            <el-option
              v-for="language in batchLanguages"
              :label="language.title"
              :value="language.value"
              :key="language.value"
              >{{ language.title }}</el-option
            >
          </el-select>
          <el-button
            @click="payDrawer = !payDrawer"
            v-if="selectedItems.length && user.scopes.includes('admin')"
            type="success"
            style="margin: 0"
            >Оплатить выбранные файлы</el-button
          >
          <el-button
            v-if="payedPage && userData && userData.batch_access"
            type="primary"
            plain
            style="margin: 0"
            @click="SendtoMarkUp"
            >Отправить в разметку</el-button
          >
          <el-button
            v-if="payedPage && userData && userData.batch_access"
            type="danger"
            style="margin: 0"
            plain
            @click="SendtoArchived"
            >Удалить</el-button
          >
          <el-button
            v-if="showDownloadButton"
            @click="download"
            :disabled="downloading"
            :icon="downloading ? 'el-icon-loading' : ''"
            type="success"
            style="margin: 0"
            plain
            >Скачать xlsx</el-button
          >
        </div>
      </div>
    </div>
    <main
      class="files-page__body flex-1 min-h-0 flex flex-col overflow-hidden"
      style="padding: 20px 26px"
    >
      <div class="flex-1 min-h-0 flex flex-col">
        <SkeletonView
          v-if="filesLoading || loading"
          :column="17"
          :row="12"
          size-table="large"
        />
        <div v-else class="flex-1 min-h-0">
          <el-table
            :data="files"
            border
            stripe
            height="100%"
            :row-class-name="tableRowClassName"
            @selection-change="handleSelection"
            ref="multiselection"
          >
            <el-table-column
              type="index"
              label="N"
              align="center"
              width="55"
            ></el-table-column>
            <el-table-column label="ID" min-width="300">
              <template #default="scope">
                {{ scope.row._id }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="user.scopes.includes('admin')"
              type="index"
              label="Имя батча"
              min-width="250"
            >
              <template #default="scope">
                {{ scope.row.topic }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="user.scopes.includes('admin')"
              type="index"
              label="Имя файла"
              min-width="250"
            >
              <template #default="scope">
                <template v-if="getFileName(scope.row.filename).length > 25">
                  <el-tooltip
                    class="item"
                    popper-class="custom-tooltip"
                    effect="dark"
                    :content="getFileName(scope.row.filename)"
                    placement="top"
                  >
                    <span>{{
                      getFileName(scope.row.filename).slice(0, 25) + "..."
                    }}</span>
                  </el-tooltip>
                </template>
                <template v-else>
                  <span>{{ getFileName(scope.row.filename) }}</span>
                </template>
              </template>
            </el-table-column>

            <el-table-column
              v-if="user.scopes.includes('admin')"
              label="Длительность файла"
              width="200"
            >
              <template #default="scope">
                <div v-if="scope.row.duration">
                  {{ timeNormalizer(scope.row.duration.mix) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Длительность фрагментов" width="220">
              <template v-slot="scope">
                {{ timeNormalizer(scope.row.duration_utterances.mix || 0) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="lang"
              :width="80"
              align="center"
              label="Язык"
            ></el-table-column>
            <el-table-column
              v-if="user.scopes.includes('admin')"
              prop="datetime"
              label="Последнее изменение"
              align="center"
              :width="220"
            >
              <template #default="scope">
                <span>
                  {{ dateFormat(scope.row.updated_at) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              v-if="user.scopes.includes('annotator') && state === '0'"
              label="Дата загрузки"
              width="200"
              align="center"
            >
              <template #default="scope">
                {{ dateFormat(scope.row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="user.scopes.includes('annotator') && state === '1'"
              prop="annotator_started"
              label="Дата начала разметки"
              align="center"
              :width="220"
            >
              <template #default="scope">
                {{ dateFormat(scope.row.annotator_started) || "-" }}
              </template>
            </el-table-column>
            <el-table-column
              prop="annotator"
              label="Статус"
              align="center"
              min-width="180"
              v-if="user.scopes.includes('admin')"
            >
              <template #default="scope">
                <div class="flex justify-center items-center">
                  <app-tag :tag="getTag(scope.row.status)" size="small" />
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="annotator"
              label="Аннотатор"
              :min-width="250"
              v-if="!user.scopes.includes('annotator')"
            >
              <template #default="scope">
                <div v-if="scope.row.annotator">
                  {{ scope.row.annotator.first_name }}
                  {{ scope.row.annotator.last_name }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="annotator_finish"
              label="Дата завершения разметки"
              :min-width="250"
              v-if="
                (user && user.scopes && user.scopes.includes('admin')) ||
                (user &&
                  user.scopes &&
                  user.scopes.includes('verifier') &&
                  state === '2') ||
                (user &&
                  user.scopes &&
                  user.scopes.includes('annotator') &&
                  (state === '2' || state === '3' || state === '4'))
              "
            >
              <template #default="scope">
                {{ dateFormat(scope.row.annotator_finish) || "-" }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="
                user &&
                user.scopes &&
                user.scopes.includes('verifier') &&
                state === '3'
              "
              label="Дата начала верификации"
              :min-width="250"
            >
              <template #default="scope">
                {{ dateFormat(scope.row.verifier_started) || "-" }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="user.scopes.includes('admin')"
              label="Длительность аннотации"
              min-width="250"
              ><template #default="scope">{{
                timeNormalizer(scope.row.annotation_duration_v2 || 0)
              }}</template></el-table-column
            >
            <el-table-column
              prop="verifier"
              label="Верификатор"
              :min-width="240"
              v-if="user.scopes.includes('admin')"
            >
              <template #default="scope">
                <div v-if="scope.row.verifier">
                  {{ scope.row.verifier.first_name }}
                  {{ scope.row.verifier.last_name }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="verifier"
              label="Дата завершения верификации"
              :min-width="250"
              v-if="
                user.scopes.includes('admin') ||
                (user.scopes.includes('verifier') && state === '4')
              "
            >
              <template #default="scope">
                {{ dateFormat(scope.row.verification_finish) || "-" }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="user.scopes.includes('admin')"
              label="Длительность верификации"
              min-width="240"
            >
              <template #default="scope">{{
                timeNormalizer(scope.row.verification_duration_v2 || 0)
              }}</template></el-table-column
            >
            <el-table-column width="100" align="center" fixed="right">
              <template #default="scope">
                <el-button
                  @click="() => viewFile(scope.row)"
                  type="primary"
                  plain
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    style="height: 12px; width: 12px"
                    viewBox="0 0 24 24"
                    fill="transparent"
                  >
                    <path
                      d="M4 22H20"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M13.8881 3.66293L14.6296 2.92142C15.8581 1.69286 17.85 1.69286 19.0786 2.92142C20.3071 4.14999 20.3071 6.14188 19.0786 7.37044L18.3371 8.11195M13.8881 3.66293C13.8881 3.66293 13.9807 5.23862 15.3711 6.62894C16.7614 8.01926 18.3371 8.11195 18.3371 8.11195M13.8881 3.66293L7.07106 10.4799C6.60933 10.9416 6.37846 11.1725 6.17992 11.4271C5.94571 11.7273 5.74491 12.0522 5.58107 12.396C5.44219 12.6874 5.33894 12.9972 5.13245 13.6167L4.25745 16.2417M18.3371 8.11195L11.5201 14.9289C11.0584 15.3907 10.8275 15.6215 10.5729 15.8201C10.2727 16.0543 9.94775 16.2551 9.60398 16.4189C9.31256 16.5578 9.00282 16.6611 8.38334 16.8675L5.75834 17.7426M5.75834 17.7426L5.11667 17.9564C4.81182 18.0581 4.47573 17.9787 4.2485 17.7515C4.02128 17.5243 3.94194 17.1882 4.04356 16.8833L4.25745 16.2417M5.75834 17.7426L4.25745 16.2417"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                  </svg>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div
          v-if="!filesLoading"
          class="mt-3 flex justify-center flex-shrink-0"
        >
          <el-pagination
            v-if="filteredTotalFiles > filters.limit"
            background
            layout="pager"
            :total="filteredTotalFiles"
            :current-page="filters.page"
            @update:current-page="handleChangePage"
            :page-size="filters.limit"
          />
        </div>
      </div>
    </main>
    <el-dialog v-model="payDrawer" title="Выберите дату">
      <el-date-picker
        v-model="formPay.date"
        type="date"
        value-format="YYYY-MM-DD"
      ></el-date-picker>
      <div class="text-right mt-5">
        <el-button :disabled="!formPay.date" @click="payFileList"
          >Оплатить</el-button
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSection } from "./shared/sections";
import { exportToExcel, getFiles } from "@/api/files";
import { getStatus as _getStatus } from "./shared/statuses";
import { format } from "date-fns";
import { getUser as _gertUser, getUsers } from "@/api/users";
import { getBatchesList } from "@/api/batches";
import { languages, timeNormalizer } from "@/api/common";
import apiClient from "@/api/api-client";
import { ElMessageBox } from "element-plus";
import { notify } from "@/services/notify";
import SkeletonView from "@/components/skeleton/SkeletonView.vue";
import AppTag from "@/components/AppTag.vue";

export default {
  components: {
    AppTag,
    SkeletonView,
  },
  props: {
    state: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      downloading: false,
      files: [],
      filesLoading: false,
      users: [],
      userData: [],
      batches: [],
      totalFiles: 0,
      totalDuration: 0,
      filteredTotalFiles: 0,
      filteredTotalDuration: 0,
      topics: [
        { key: "egov", title: "Egov" },
        { key: "tson", title: "ЦОН" },
        { key: "sp", title: "По пенсионным вопросам" },
        { key: "ashyq", title: "Ashyq" },
        { key: "sl", title: "По трудом вопросам" },
      ],
      importForm: {
        topic: null,
        lang: null,
        limit: null,
      },
      loadingImportFiles: false,
      importFormRules: {
        limit: [
          {
            required: true,
            message: "Это обязательное поле",
            trigger: ["blur", "change"],
          },
          {
            max: 100,
            min: 1,
            message: "Количество файлов должна быть меньше 100",
            trigger: ["blur", "change"],
          },
        ],
      },
      filters: {
        page: 1,
        limit: 20,
        status: null,
        annotator: null,
        lang: null,
        is_model: null,
        file_id: "",
        batch_id: null,
        date: [],
        action: "",
        sort: "updated_at:desc",
      },
      importDrawer: false,
      importFiles: [],
      selectedItems: [],
      selectedItemsImport: [],
      formPay: {
        selectedFiles: [],
        date: "",
      },
      payDrawer: false,
      analytics: {},
    };
  },
  computed: {
    sidebarItem() {
      return getSection(this.state === "all" ? this.state : +this.state);
    },
    pageTitle() {
      return this.sidebarItem.title;
    },
    fileId() {
      return this.$route.query.fileId;
    },
    payedPage() {
      if (this.state === "all") return false;
      return +this.state === 5;
    },
    user() {
      return this.$store.state.user.user;
    },
    showDownloadButton() {
      return (
        this.user.scopes.includes("admin") ||
        this.user.scopes.includes("verifier")
      );
    },
    batchLanguages() {
      return languages();
    },
  },
  created() {
    this.setSortOrder();
  },
  async mounted() {
    this.setInitialFilters();
    await this.getUser();
    await this.getFiles();
    await this.getBatches();
    this.users = await getUsers();
  },
  watch: {
    async state(newState) {
      this.setFiltersForState(newState);
      this.setSortOrder();
      await this.getFiles();
    },
  },
  methods: {
    setInitialFilters() {
      if (this.state !== "all") {
        this.filters.status = +this.state;
      }
      // Prefill the file-id filter when opened from the command palette
      if (this.$route.query.file_id) {
        this.filters.file_id = String(this.$route.query.file_id);
      }
    },
    setSortOrder() {
      if (this.state === "0") {
        this.filters.sort = "updated_at:asc";
      } else {
        this.filters.sort = "updated_at:desc";
      }
      if (
        (this.state === "2" || this.state === "3" || this.state === "4") &&
        this.user.scopes.includes("admin")
      ) {
        this.filters.sort = "annotator_finish:desc";
      }
      if (this.state === "0" && this.user.scopes.includes("annotator")) {
        this.filters.sort = "created_at:asc";
      }
      if (this.state === "1" && this.user.scopes.includes("annotator")) {
        this.filters.sort = "annotator_started:desc";
      }
      if (
        (this.state === "2" || this.state === "3" || this.state === "4") &&
        this.user.scopes.includes("annotator")
      ) {
        this.filters.sort = "annotator_finish:desc";
      }
      if (this.state === "2" && this.user.scopes.includes("verifier")) {
        this.filters.sort = "annotator_finish:desc";
      }
      if (this.state === "3" && this.user.scopes.includes("verifier")) {
        this.filters.sort = "verifier_started:desc";
      }
      if (this.state === "4" && this.user.scopes.includes("verifier")) {
        this.filters.sort = "verification_finish:desc";
      }
    },
    setFiltersForState() {
      if (this.state !== "all") {
        this.filters.status = +this.state;
      } else {
        this.filters.status = undefined;
      }
      this.filters.lang = null;
      this.filters.page = 1;
      this.filters.batch_id = null;
    },
    getFileName(filename) {
      if (filename) {
        return filename.split("/").pop();
      }
    },
    async getUser() {
      this.loading = true;
      const user = await _gertUser(this.user._id);
      this.loading = false;
      this.userData = user || [];
    },
    async getBatches() {
      this.loading = true;
      try {
        const { data } = await getBatchesList();
        this.batches = data || [];
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async handleChangePage(val) {
      this.filters.page = val;
      await this.getFiles();
    },
    tableRowClassName({ row }) {
      if (this.fileId === row._id) {
        return "bg-blue-50";
      }
      return "";
    },
    async SendtoMarkUp() {
      try {
        await ElMessageBox.confirm(
          "Вы уверены, что хотите отправить в разметку?",
          "Подтверждение",
          {
            confirmButtonText: "Да",
            cancelButtonText: "Отмена",
            type: "warning",
          }
        );

        const json = {}; // Пустой JSON-объект
        const response = await apiClient.post("/files", json, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.data._success) {
          notify({
            message: "Запрос успешно отправлен!",
            type: "success",
          });
          window.location.reload();
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error(error);
          notify({
            message: "Ошибка при отправке запроса",
            type: "error",
          });
        }
      }
    },
    async SendtoArchived() {
      try {
        await ElMessageBox.confirm(
          "Вы уверены, что хотите удалить?",
          "Подтверждение",
          {
            confirmButtonText: "Да",
            cancelButtonText: "Отмена",
            type: "warning",
          }
        );

        const response = await apiClient.post("/files", {
          action: "delete",
        });

        if (response.data._success) {
          notify({
            message: "Файл успешно архивирован!",
            type: "success",
          });
          window.location.reload();
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error("Ошибка при отправке запроса:", error);
          notify({
            message: "Ошибка при отправке запроса",
            type: "error",
          });
        }
      }
    },
    async getFiles() {
      this.filesLoading = true;
      try {
        const filters = this.getFilters();
        const data = await getFiles(filters);

        this.analytics = data.analytics;
        this.files = data.data.map((k) => ({
          ...k,
          is_model: "is_model" in k ? k.is_model : 0,
        }));

        this.totalFiles = data.general.total;
        this.totalDuration = data.general.seconds || 0;
        this.filteredTotalFiles = data.filtered.total || 0;
        this.filteredTotalDuration = data.filtered.seconds || 0;
      } catch (error) {
        console.error(error);
      } finally {
        setTimeout(() => {
          this.filesLoading = false;
        }, 300);
      }
    },
    handleResetFilter(key, reset_value) {
      this.filters.page = 1;
      this.filters[key] = reset_value;
      this.getFiles();
    },
    viewFile(row) {
      this.$router.push({
        name: "file-view",
        params: { state: row.status, fileId: row._id },
      });
    },
    timeNormalizer(timeInSeconds) {
      return timeNormalizer(timeInSeconds);
    },
    getStatusTitle(state) {
      const status = _getStatus(state);
      return (status && status.title) || state;
    },
    getTag(statusId) {
      const statusText = this.getStatusTitle(statusId);
      let color = "#9CA3AF";

      switch (statusId) {
        case 1:
          color = "#FBBF24";
          break;
        case 2:
          color = "#3B82F6";
          break;
        case 3:
          color = "#22C55E";
          break;
        case 4:
          color = "#6366F1";
          break;
        case 5:
          color = "#9CA3AF";
          break;
      }

      return { value: statusText, color };
    },
    dateFormat(date) {
      if (!date) return "";
      return format(new Date(date), "dd MMM yyyy, HH:mm");
    },
    async download() {
      this.downloading = true;
      let params = {};
      if (this.filters.date && this.filters.date.length > 1) {
        params.start_date = this.filters.date[0];
        params.stop_date = this.filters.date[1];
      }
      await exportToExcel({
        status: +this.state,
        annotator: this.filters.annotator,
        ...params,
      });
      this.downloading = false;
    },
    handleSelection(val) {
      console.log(val);
      this.selectedItems = val.map((k) => k._id);
    },
    getFilters() {
      let params = { ...this.filters };
      const query = this.filters.file_id?.trim();
      if (query) {
        params.file_id = query;
      } else {
        delete params.file_id;
      }
      if (this.filters.date && this.filters.date.length > 1) {
        params.start_date = this.filters.date[0];
        params.stop_date = this.filters.date[1];
      }
      delete params.action;
      if (this.filters.action) {
        params.action = this.filters.action;
      }
      if (this.filters.batch_id) {
        params.batch_id = this.filters.batch_id;
      }
      if (this.filters.is_model) {
        params.is_model = this.filters.is_model;
      }
      delete params.date;
      return params;
    },
  },
};
</script>

<style scoped lang="scss">
/* Gentle mount fade — runs once per route enter, doesn't touch the router */
.files-page {
  animation: files-fade-in 0.3s cubic-bezier(0.22, 1, 0.36, 1) both;
}
@keyframes files-fade-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .files-page {
    animation: none;
  }
}

/* ===== Taskk-style page header ===== */
.files-page__header {
  padding: 10px 26px 0;
}

.files-page__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.files-page__title {
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.8px;
  color: var(--foreground);
  line-height: 1.1;
}

.files-page__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  background: var(--muted);
  border: 1px solid var(--border);
  color: var(--muted-foreground);
  font-size: 13px;
  font-weight: 600;
}

// thin separator dot between count and duration
.files-page__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--border-strong);
}

.files-page__duration {
  display: inline-flex;
  align-items: center;
  gap: 9px;

  &-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--muted-foreground);
  }
  &-value {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 11px;
    border-radius: 999px;
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.25);
    color: #3b82f6;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;

    svg {
      width: 13px;
      height: 13px;
    }
  }
}

:global(html.dark) .files-page__duration-value {
  background: rgba(59, 130, 246, 0.16);
  border-color: rgba(59, 130, 246, 0.32);
  color: #7eb0ff;
}

.files-page__filters {
  margin-top: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}

/* ===== shadcn-style filter controls (Element Plus overrides) ===== */
.files-page__filters {
  // shared field shell (select / input / date-picker)
  ::v-deep(.el-input__wrapper),
  ::v-deep(.el-date-editor.el-input__wrapper),
  ::v-deep(.el-range-editor.el-input__wrapper) {
    height: 38px;
    border-radius: 9px;
    background: var(--card);
    box-shadow: 0 0 0 1px var(--input) inset;
    padding: 1px 12px;
    transition: box-shadow 0.18s ease, background-color 0.18s ease;
  }

  ::v-deep(.el-input__wrapper:hover),
  ::v-deep(.el-date-editor.el-input__wrapper:hover),
  ::v-deep(.el-range-editor.el-input__wrapper:hover) {
    box-shadow: 0 0 0 1px var(--border-strong) inset;
    background: var(--accent);
  }

  // focus ring — subtle, no harsh blue
  ::v-deep(.el-input__wrapper.is-focus),
  ::v-deep(.el-input.is-focus .el-input__wrapper),
  ::v-deep(.el-range-editor.is-active),
  ::v-deep(.el-range-editor.is-active:hover) {
    background: var(--card);
    box-shadow: 0 0 0 1px var(--border-strong) inset, 0 0 0 3px var(--ring);
  }

  ::v-deep(.el-input__inner) {
    color: var(--foreground);
    font-size: 13px;
    font-weight: 500;
    &::placeholder {
      color: var(--muted-foreground);
      font-weight: 500;
    }
  }

  ::v-deep(.el-range-input) {
    color: var(--foreground);
    font-size: 13px;
    background: transparent;
    &::placeholder {
      color: var(--muted-foreground);
    }
  }
  ::v-deep(.el-range-separator) {
    color: var(--muted-foreground);
  }

  // dropdown / clear / date icons
  ::v-deep(.el-select__caret),
  ::v-deep(.el-input__icon),
  ::v-deep(.el-range__icon),
  ::v-deep(.el-range__close-icon) {
    color: var(--muted-foreground);
  }

  // buttons — soft neutral shadcn look, smooth hover
  ::v-deep(.el-button) {
    height: 38px;
    border-radius: 9px;
    border: 1px solid var(--border);
    background: var(--card);
    color: var(--foreground);
    font-size: 13px !important;
    font-weight: 500 !important;
    padding: 0 16px;
    transition: background-color 0.18s ease, border-color 0.18s ease,
      color 0.18s ease, box-shadow 0.18s ease;

    &:hover {
      background: var(--accent);
      border-color: var(--border-strong);
    }
    &:active {
      background: var(--muted);
    }
  }

  ::v-deep(.el-button--success),
  ::v-deep(.el-button--primary) {
    border-color: var(--border);
    color: var(--foreground);
    &:hover {
      border-color: var(--border-strong);
      background: var(--accent);
    }
  }
  ::v-deep(.el-button--danger) {
    color: #ef4444;
    &:hover {
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.4);
      color: #ef4444;
    }
  }
}

.el-table .bg-blue-50 {
  background: rgba(59, 130, 246, 0.12);
}
::v-deep(.el-table__header th.el-table__cell) {
  background: var(--muted);
  color: var(--muted-foreground);
  font-weight: 500;
  font-size: 12px;
}
// el-table already draws its own borders (border prop) — round the outer
// corners cleanly. Hide el-table's absolutely-positioned outer border lines
// (the ::before bottom line / right patch) that get clipped at the rounded
// corners and read as a "cut" border.
.files-page__body ::v-deep(.el-table) {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);

  &::before,
  &::after {
    display: none;
  }
  .el-table__inner-wrapper::before,
  .el-table__border-left-patch {
    display: none;
  }
}

// Pagination — active page uses primary token (black in light, white in dark)
::v-deep(.el-pagination.is-background .el-pager li.is-active),
::v-deep(.el-pagination.is-background .el-pager li.active) {
  background-color: var(--primary) !important;
  color: var(--primary-foreground) !important;
}
::v-deep(.el-pagination.is-background .el-pager li:not(.is-active):hover) {
  color: var(--foreground);
}
</style>
